import { useCallback, useEffect, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import {
  DayOfWeek,
  Event,
  RecurringEventFrequency,
} from '@tribeplatform/gql-client/types'
import { DateValue } from '@tribeplatform/react-ui-kit/DateTimePicker'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'

import { dayjs } from '../../../common/lib/dayjs.js'
import { FormDateTime } from '../../../Form/FormDateTime/index.js'
import { Form } from '../../../Form/index.js'
import { T, useStaticIntl, useI18n } from '../../../i18n/index.js'
import { isRecurringEvent } from '../../EventDetails/utils.js'
import {
  getDayOfWeekFromDate,
  sortDaysByWeekOrder,
  useDaysOfWeekItems,
} from '../../hooks/useDaysOfWeekItems.js'
import { frequencyItemsFactory } from '../utils.js'

interface RecurrenceConfigProps {
  event?: Event
}

export const RecurrenceConfig = ({ event }: RecurrenceConfigProps) => {
  const { control, resetField, setValue, trigger, watch } = useFormContext()
  const { $t } = useI18n()
  const frequencyItems = useStaticIntl(frequencyItemsFactory)
  const daysOfWeekItems = useDaysOfWeekItems()

  const startTime = useWatch({ control, name: 'startTime' })
  const endTime = useWatch({ control, name: 'endTime' })
  const frequency = useWatch({
    control,
    name: 'recurrenceConfig.frequency',
  }) as RecurringEventFrequency | undefined
  const recurrenceEndDate = useWatch({
    control,
    name: 'recurrenceConfig.recurrenceEndDate',
  }) as Date | undefined

  useEffect(() => {
    const subscription = watch((formValues, { name }) => {
      const isNewEvent = !event
      // To avoid undesired changes to existing events, it's reset
      // only for the new events.
      if (name === 'startTime' && isNewEvent) {
        const { startTime: newStartTime, recurrenceConfig: config } = formValues
        if (
          newStartTime &&
          config?.frequency === RecurringEventFrequency.weekly
        ) {
          const dayOfWeek = getDayOfWeekFromDate(newStartTime)
          setValue('recurrenceConfig.repeatsOn', [dayOfWeek], {
            shouldDirty: true,
          })
        }
      }

      if (name === 'endTime') {
        const { endTime: newEndTime, recurrenceConfig: config } = formValues
        const isRecurrenceEndDateBeforeEndTime = dayjs(
          config?.recurrenceEndDate,
        ).isBefore(dayjs(newEndTime))

        if (isRecurrenceEndDateBeforeEndTime) {
          setValue('recurrenceConfig.recurrenceEndDate', null, {
            shouldDirty: true,
          })
          trigger('recurrenceConfig.recurrenceEndDate')
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, setValue, trigger, event])

  const handleFrequencyChange = useCallback(
    (newFrequency: RecurringEventFrequency) => {
      if (!newFrequency || newFrequency === RecurringEventFrequency.once) {
        setValue('recurrenceConfig.frequency', RecurringEventFrequency.once, {
          shouldDirty: true,
        })
        setValue('recurrenceConfig.repeatsOn', null)
        setValue('recurrenceConfig.recurrenceEndDate', null)
        return
      }

      if (
        isRecurringEvent(event) &&
        newFrequency === event.recurrenceConfig.frequency
      ) {
        resetField('recurrenceConfig', { keepDirty: false })
        return
      }

      setValue('recurrenceConfig.frequency', newFrequency, {
        shouldDirty: true,
      })

      if (newFrequency === RecurringEventFrequency.weekly) {
        const dayOfWeek = startTime
          ? getDayOfWeekFromDate(startTime)
          : DayOfWeek.MONDAY
        setValue('recurrenceConfig.repeatsOn', [dayOfWeek], {
          shouldDirty: true,
        })
      } else {
        setValue('recurrenceConfig.repeatsOn', undefined)
      }
    },
    [event, resetField, setValue, startTime],
  )

  const handleEndDateChange = useCallback(
    (date: DateValue) => {
      setValue('recurrenceConfig.recurrenceEndDate', dayjs(date).toDate(), {
        shouldDirty: true,
      })
    },
    [setValue],
  )

  const isRecurring = frequency && frequency !== RecurringEventFrequency.once
  const isWeekly = frequency === RecurringEventFrequency.weekly
  const forceSelectedDayOfWeek = useMemo(() => {
    if (!startTime || !!event) {
      return undefined
    }
    return getDayOfWeekFromDate(startTime)
  }, [startTime, event])

  return (
    <div className="flex flex-col gap-6">
      <FormControl.Select
        value={frequency ?? RecurringEventFrequency.once}
        onChange={handleFrequencyChange}
        name="recurrenceConfig.frequency"
        label={
          <T
            id="Event.Form.Recurrence.Repeats.Title"
            defaultMessage="Repeats"
          />
        }
        items={frequencyItems}
      />
      {isRecurring && (
        <div className="flex flex-col gap-6">
          {isWeekly && (
            <RepeatsOnPicker
              daysOfWeekItems={daysOfWeekItems}
              forceSelectedItem={forceSelectedDayOfWeek}
            />
          )}
          <FormDateTime
            type="date"
            className="w-full"
            dateFormat="M j, Y"
            name="recurrenceConfig.recurrenceEndDate"
            minDate={dayjs(endTime).toDate()}
            label={
              <T
                id="Event.Form.Recurrence.EndRepeatOn"
                defaultMessage="Ends repeat on"
              />
            }
            value={recurrenceEndDate}
            onChange={handleEndDateChange}
            validation={{
              required: $t({
                id: 'Generic.FieldIsRequired',
                defaultMessage: 'This field is required.',
              }),
            }}
            helperText={
              recurrenceEndDate && (
                <T
                  id="Event.Form.Recurrence.EndRepeatOn.HelpText"
                  defaultMessage="Ends after the session on {date}."
                  values={{
                    date: dayjs(recurrenceEndDate).format('MMM D, YYYY'),
                  }}
                />
              )
            }
          />
        </div>
      )}
    </div>
  )
}

interface RepeatsOnPickerProps {
  daysOfWeekItems: Array<{
    value: DayOfWeek
    text: string
    readableText: string
  }>
  forceSelectedItem?: DayOfWeek
}

const RepeatsOnPicker = ({
  daysOfWeekItems,
  forceSelectedItem,
}: RepeatsOnPickerProps) => {
  const { control, setValue } = useFormContext()
  const { $t, formatList } = useI18n()

  const repeatsOn = useWatch({
    control,
    name: 'recurrenceConfig.repeatsOn',
  }) as DayOfWeek[] | undefined

  const handleChange = useCallback(
    (days: DayOfWeek[]) => {
      const otherItems = days.filter(day => day !== forceSelectedItem)
      const newDays = forceSelectedItem
        ? [forceSelectedItem, ...otherItems]
        : otherItems
      setValue('recurrenceConfig.repeatsOn', newDays, { shouldDirty: true })
    },
    [forceSelectedItem, setValue],
  )

  const helperText = useMemo(() => {
    if (!repeatsOn?.length) {
      return null
    }

    /**
     * Sort days in weekday order for consistent display
     */
    const sortedDays = sortDaysByWeekOrder(repeatsOn)
    const dayNames = sortedDays
      .map(
        day => daysOfWeekItems.find(item => item.value === day)?.readableText,
      )
      .filter(Boolean) as string[]

    return (
      <T
        id="Event.Form.Recurrence.RepeatsOn.HelpText"
        defaultMessage="Event will repeat on {days}."
        values={{ days: formatList(dayNames) }}
      />
    )
  }, [repeatsOn, daysOfWeekItems, formatList])

  const lockedItems = useMemo(() => {
    if (!forceSelectedItem) {
      return []
    }

    return [
      {
        value: forceSelectedItem,
        helper: (
          <T
            id="Event.Form.Recurrence.RepeatsOn.DisabledItemHelper"
            defaultMessage="The event starts on this day, so it must be included."
          />
        ),
      },
    ]
  }, [forceSelectedItem])

  return (
    <Form.ButtonGroupSelect
      fullWidth
      name="recurrenceConfig.repeatsOn"
      selectedVariant="primary"
      label={
        <T id="Event.Form.Recurrence.RepeatsOn" defaultMessage="Repeats on" />
      }
      validation={{
        required: $t({
          id: 'Generic.FieldIsRequired',
          defaultMessage: 'This field is required.',
        }),
      }}
      value={repeatsOn ?? []}
      mode="multiple"
      onChange={handleChange}
      lockedItems={lockedItems}
      items={daysOfWeekItems}
      helperText={helperText}
    />
  )
}
